/**
 * Without setting a maximum recursion depth for ObjectPaths, Typescript will fail compiling with the error
 * "Type instantiation is excessively deep and possibly infinite".
 *
 * By using an array that's offset by 1 (MaxDepth[4] = 3, MaxDepth[0] = never), we can use it as a counter
 * by passing the value into the next recursion and bail out if we ever hit `never`. Typescript can statically
 * analyze the maximum recursion depth with this approach which solves the issue of infinite recursion depth.
 */
type MaxDepth = [never, 0, 1, 2, 3, 4, 5, 6, 7, 8, 9];

// Produces a string literal union that contains every traversable path in an object.
export type ObjectPaths<T extends {[key: string]: any}, D extends MaxDepth[number] = 9> = D extends [never]
    ? // Bail out if we hit maximum recursion depth. This speeds up TS compilation time significantly
      never
    : {
          // Test if T[K] is an object
          [K in keyof T]: T[K] extends {[key: string]: any}
              ? // If T[K] is an object then return a string literal of K and ObjectPaths applied to T[K]
                `${string & K}.${string & ObjectPaths<T[K], MaxDepth[D]>}`
              : // Otherwise return K
                K;
      }[keyof T]; // Map over each key of T

// Access the type of a value in an object by using an object path delimited by ".", i.e. `"foo.bar.baz"`
export type TraverseObjectByPath<
    T extends {[key: string]: any},
    Path extends string,
> = Path extends `${infer K}.${infer Rest}` ? TraverseObjectByPath<T[K], Rest> : T[Path];

/**
 * Functional wrapper over Object.values that provides automatic type inference.
 * @param obj The object to extract values from
 * @returns An array of values from the object
 */
export const values = <T extends Record<string, any>>(obj: T): T[keyof T][] => Object.values(obj);
